/**
 * Breadcrumb provider + hook.
 *
 * Pages call `useBreadcrumbs([{ label, to }, ...])` from a `useEffect`
 * to register their trail; the AppShell reads the current trail from
 * the same context and renders it under the top nav. Calling
 * `useBreadcrumbs([])` (or unmounting the page) clears them.
 */
import { ChevronRight } from "lucide-react";
import { type ReactNode, createContext, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { cn } from "../lib/cn";
import { Button } from "./ui";

export interface Crumb {
  label: string;
  to?: string;
}

interface BreadcrumbContextValue {
  trail: Crumb[];
  setTrail: (trail: Crumb[]) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null);

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [trail, setTrail] = useState<Crumb[]>([]);
  const value = useMemo(() => ({ trail, setTrail }), [trail]);
  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}

export function useBreadcrumbs(crumbs: Crumb[]): void {
  const ctx = useContext(BreadcrumbContext);
  if (!ctx) {
    throw new Error("useBreadcrumbs must be used inside <BreadcrumbProvider>");
  }
  const key = JSON.stringify(crumbs);
  // The effect deps array is the stringified crumbs so callers can
  // build the array inline without a useMemo on their end.
  useEffect(() => {
    ctx.setTrail(crumbs);
    return () => ctx.setTrail([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

export function Breadcrumbs({ className }: { className?: string }) {
  const ctx = useContext(BreadcrumbContext);
  const trail = ctx?.trail ?? [];
  if (trail.length === 0) return null;
  // Wave-6F.1 (#488) — each navigable crumb renders as a ghost-variant
  // button (per master plan §13.1 item 4: founder asked for visible
  // button affordance so people know the segments are clickable). The
  // last crumb is the current page so it renders as a plain label
  // (non-interactive), keeping the visual hierarchy honest. The shared
  // `<Button/>` component carries the focus ring + hover tint
  // consistent with the rest of the chrome.
  return (
    <nav aria-label="Breadcrumb" className={cn("text-sm text-fg-subtle", className)}>
      <ol className="flex flex-wrap items-center gap-1">
        {trail.map((crumb, i) => {
          const isLast = i === trail.length - 1;
          return (
            <li key={`${crumb.label}-${i}`} className="flex items-center gap-1">
              {i > 0 ? (
                <ChevronRight className="h-3.5 w-3.5 text-fg-faint" aria-hidden="true" />
              ) : null}
              {crumb.to && !isLast ? (
                <Button asChild variant="ghost" size="sm" className="h-7 px-2 text-xs font-normal">
                  <Link to={crumb.to}>{crumb.label}</Link>
                </Button>
              ) : (
                <span
                  aria-current={isLast ? "page" : undefined}
                  className={cn(
                    "px-2 py-1 text-xs",
                    isLast ? "font-medium text-fg" : "text-fg-muted",
                  )}
                >
                  {crumb.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
